/**
=========================================================
* Trust Cargo - change one order's status
=========================================================

* The create form does not offer STATUS: an order is born NEW and is moved
* on from here. Until now the only way to move one was to tick it and use
* the bulk "Set status" above the table, which is a lot of ceremony for the
* single row you are looking at - so the row carries its own button.
*
* It writes through the same endpoint the bulk change uses, with a list of
* one, so a status change looks the same to the server wherever it came
* from.
*/

import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Icon from "@mui/material/Icon";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";

const API = "https://trust-cargo.co/anas_backend/api/v2";

function OrderStatusDialog({ open, order, statuses, onClose, onSaved }) {
  const [picked, setPicked] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  /**
   * Start on the status the order is already on.
   *
   * The list rows carry the status as a word rather than an id - it is what
   * the table prints - so it is matched back by name against the office's
   * list. An order whose status was since renamed starts on nothing, which
   * is honest: there is no id to send for it.
   */
  useEffect(() => {
    if (!open) return;

    setError("");
    setSaving(false);

    const byId = order && order.status_id;
    if (byId !== undefined && byId !== null && byId !== "") {
      setPicked(String(byId));
      return;
    }

    const name = String((order && order.status) || "")
      .trim()
      .toLowerCase();
    const here = statuses.find(
      (s) =>
        String(s.name || "")
          .trim()
          .toLowerCase() === name
    );

    setPicked(here ? String(here.id) : "");
  }, [open, order && order.id]);

  const save = async () => {
    if (!picked) return;

    setSaving(true);
    setError("");

    try {
      await axios.post(
        `${API}/orders/bulk-status`,
        { ids: [order.id], status_id: picked },
        { headers: { Accept: "application/json", "Content-Type": "application/json" } }
      );
      setSaving(false);
      onSaved();
      onClose();
    } catch (e) {
      setSaving(false);
      setError(e?.response?.data?.message || e?.message || "Could not change the status");
    }
  };

  if (!order) return null;

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: ({ borders, boxShadows }) => ({
          borderRadius: borders.borderRadius.lg,
          boxShadow: boxShadows.xl,
        }),
      }}
    >
      <DialogTitle
        sx={({ palette, functions: { rgba } }) => ({
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${rgba(palette.dark.main, 0.08)}`,
          pb: 1.5,
        })}
      >
        <SuiTypography variant="h5" fontWeight="bold">
          Change Status
        </SuiTypography>
        <Icon
          onClick={saving ? undefined : onClose}
          sx={({ palette, functions: { rgba } }) => ({
            cursor: saving ? "default" : "pointer",
            fontSize: 26,
            color: rgba(palette.dark.main, 0.45),
            "&:hover": { color: palette.error.main },
          })}
        >
          close
        </Icon>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {error && (
          <SuiBox mb={2}>
            <Alert severity="error">{error}</Alert>
          </SuiBox>
        )}

        {/* which order this is - the shop code is how the office says it */}
        <SuiTypography sx={{ fontSize: 13, opacity: 0.7, mb: 2 }}>
          {`Order ${order.shop_code || `#${order.id}`}`}
          {order.customer ? ` - ${order.customer}` : ""}
          {order.status ? ` - now ${order.status}` : ""}
        </SuiTypography>

        {statuses.length === 0 && (
          <Alert severity="warning">The status list could not be loaded</Alert>
        )}

        {/**
         * The whole list on show rather than a dropdown. There are five of
         * them, and the point of this button is one click on the row in
         * front of you - a select would put the choice two clicks away
         * again.
         */}
        <SuiBox sx={{ display: "grid", gap: 1 }}>
          {statuses.map((s) => {
            const on = String(s.id) === String(picked);

            return (
              <SuiBox
                key={s.id}
                component="button"
                type="button"
                onClick={() => setPicked(String(s.id))}
                disabled={saving}
                aria-pressed={on}
                sx={({ palette, borders, functions: { rgba } }) => ({
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  width: "100%",
                  px: 2,
                  py: 1.5,
                  cursor: saving ? "default" : "pointer",
                  textAlign: "left",
                  borderRadius: borders.borderRadius.md,
                  border: `1px solid ${rgba(palette.dark.main, on ? 0 : 0.14)}`,
                  borderColor: on ? palette.primary.main : undefined,
                  backgroundColor: on ? rgba(palette.primary.main, 0.12) : "transparent",
                  color: palette.dark.main,
                  transition: "background-color 150ms ease, border-color 150ms ease",
                  "&:hover": { borderColor: rgba(palette.primary.main, 0.5) },
                })}
              >
                <Icon
                  sx={({ palette, functions: { rgba } }) => ({
                    fontSize: "20px !important",
                    color: on ? palette.primary.main : rgba(palette.dark.main, 0.3),
                  })}
                >
                  {on ? "radio_button_checked" : "radio_button_unchecked"}
                </Icon>
                <SuiTypography sx={{ fontSize: 15, fontWeight: on ? 700 : 500 }}>
                  {s.name}
                </SuiTypography>
              </SuiBox>
            );
          })}
        </SuiBox>
      </DialogContent>

      <DialogActions
        sx={({ palette, functions: { rgba } }) => ({
          px: 3,
          pb: 2.5,
          pt: 1,
          gap: 1.5,
          borderTop: `1px solid ${rgba(palette.dark.main, 0.07)}`,
        })}
      >
        <Button
          onClick={onClose}
          disabled={saving}
          size="large"
          sx={{ minWidth: 110, fontSize: 15 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={save}
          disabled={saving || !picked}
          sx={{ minWidth: 140, fontSize: 15 }}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

OrderStatusDialog.defaultProps = { order: null, statuses: [], onSaved: () => {} };

OrderStatusDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  // the row from the orders list - it carries the status as a word
  order: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    shop_code: PropTypes.string,
    customer: PropTypes.string,
    status: PropTypes.string,
    status_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  // the office's list, {id, name} - loaded by the screen that opens this
  statuses: PropTypes.arrayOf(PropTypes.object),
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
};

export default OrderStatusDialog;
